// Persiste a keypair da estação de trabalho no IndexedDB.
//
// Por que IndexedDB e não localStorage: localStorage só guarda strings, então
// teríamos que exportar a privada — perdendo a proteção `extractable: false`.
// IndexedDB aceita o `CryptoKey` como objeto opaco, mantendo a privada
// inacessível por código JS.
//
// O par de chaves é **um por navegador**, não um por e-mail — uma máquina
// física é uma workstation só, independente de quem loga nela.
// `loadDeviceKeyPair`/`saveDeviceKeyPair`/`clearDeviceKeyPair` abaixo
// leem/escrevem sempre o mesmo registro fixo (`DEVICE_RECORD_ID`).
//
// O esquema antigo (indexado por e-mail: `loadKeyPair`/`saveKeyPair`/
// `clearKeyPair`/`normalizeUsername`) foi removido — sem referências fora dos
// próprios testes já deletados junto. Não recriar consumidores por e-mail.

const DB_NAME = 'kiper-workstation-auth';
const DB_VERSION = 1;
const STORE_NAME = 'keypair';

// Chave fixa do registro único por navegador.
const DEVICE_RECORD_ID = '__device__';

const openDb = () =>
  new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const withStore = async (mode, callback) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, mode);
    const store = transaction.objectStore(STORE_NAME);
    let result;
    transaction.oncomplete = () => {
      db.close();
      resolve(result);
    };
    transaction.onerror = () => {
      db.close();
      reject(transaction.error);
    };
    result = callback(store);
  });
};

export const loadDeviceKeyPair = async () => {
  try {
    const record = await withStore(
      'readonly',
      store =>
        new Promise((resolve, reject) => {
          const request = store.get(DEVICE_RECORD_ID);
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        }),
    );
    if (!record) return null;
    return {
      privateKey: record.privateKey,
      publicKey: record.publicKey,
      publicKeyJwk: record.publicKeyJwk,
      thumbprint: record.thumbprint,
      createdAt: record.createdAt,
    };
  } catch (error) {
    return null;
  }
};

export const saveDeviceKeyPair = async ({
  privateKey,
  publicKey,
  publicKeyJwk,
  thumbprint,
}) =>
  withStore('readwrite', store =>
    store.put({
      id: DEVICE_RECORD_ID,
      privateKey,
      publicKey,
      publicKeyJwk,
      thumbprint,
      createdAt: new Date().toISOString(),
    }),
  );

export const clearDeviceKeyPair = async () =>
  withStore('readwrite', store => store.delete(DEVICE_RECORD_ID));
